import React from "react";
import { Dialog, DialogContent, Typography, Stack, Box } from "@mui/material";
import ContainedButton from "../../forms/button/ContainedButton";
import { UnifiedIcon } from "../Icon/UnifiedIcon";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoAlertCircle,
} from "react-icons/io5";

interface StatusModalProps {
  open: boolean;
  onClose: () => void;
  type: "success" | "error" | "warning";
  title?: string;
  message: string;
  actions?: {
    primary?: {
      label: string;
      onClick: () => void;
    };
    secondary?: {
      label: string;
      onClick: () => void;
    };
    tertiary?: {
      label: string;
      onClick: () => void;
    };
  };
}

const StatusModal: React.FC<StatusModalProps> = ({
  open,
  onClose,
  type,
  title,
  message,
  actions,
}) => {
  const getIcon = (): JSX.Element => {
    switch (type) {
      case "success":
        return (
          <UnifiedIcon icon={IoCheckmarkCircle} fontSize={60} color="#4CAF50" />
        );
      case "error":
        return (
          <UnifiedIcon icon={IoCloseCircle} fontSize={60} color="#F44336" />
        );
      case "warning":
        return (
          <UnifiedIcon icon={IoAlertCircle} fontSize={60} color="#FF9800" />
        );
      default:
        return (
          <UnifiedIcon icon={IoCheckmarkCircle} fontSize={60} color="#4CAF50" />
        );
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case "success":
        return "#4CAF50";
      case "error":
        return "#F44336";
      case "warning":
        return "#FF9800";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          padding: "20px",
          textAlign: "center",
        },
        zIndex: 2000,
      }}
    >
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          <Box sx={{ mb: 1 }}>{getIcon()}</Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: getTitleColor(),
              fontFamily: "Figtree",
            }}
          >
            {title || type.charAt(0).toUpperCase() + type.slice(1)}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              fontFamily: "Figtree",
              mb: 2,
            }}
          >
            {message}
          </Typography>

          {actions ? (
            <Stack spacing={2} sx={{ width: "100%" }}>
              {actions.primary && (
                <ContainedButton
                  label={actions.primary.label}
                  onClick={actions.primary.onClick}
                  sx={{
                    width: "100%",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#1064ED",
                    "&:hover": {
                      backgroundColor: "#0d52c1",
                    },
                  }}
                />
              )}
              {actions.secondary && (
                <ContainedButton
                  label={actions.secondary.label}
                  onClick={actions.secondary.onClick}
                  sx={{
                    width: "100%",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#1064ED",
                    "&:hover": {
                      backgroundColor: "#0d52c1",
                    },
                  }}
                />
              )}
              {actions.tertiary && (
                <Typography
                  variant="body2"
                  onClick={actions.tertiary.onClick}
                  sx={{
                    color: "#1064ED",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: 500,
                    mt: 1,
                  }}
                >
                  {actions.tertiary.label}
                </Typography>
              )}
            </Stack>
          ) : (
            <ContainedButton
              label="Okay"
              onClick={onClose}
              sx={{
                width: "100%",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: "#1064ED",
                "&:hover": {
                  backgroundColor: "#0d52c1",
                },
              }}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default StatusModal;
